package com.arena_web.ui.sections.arena.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.ClubDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.arena.ArenaEvent
import com.arena_web.ui.sections.arena.EventStatus
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mPaddingTop
import com.arena_web.ui.utils.mWidth
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

@Composable
fun ArenaContentMobile(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    events: List<ArenaEvent>,
    isLoading: Boolean,
    isLoadingMore: Boolean = false,
    onAddEvent: () -> Unit,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {},
    onMapClick: () -> Unit = {},
    clubs: List<ClubDto> = emptyList(),
    isLoadingClubs: Boolean = false,
    isLoadingMoreClubs: Boolean = false,
    onLoadMoreClubs: () -> Unit = {},
    onClubClick: (ClubDto) -> Unit = {},
    filterCount: Int = 0,
    onFilterClick: () -> Unit = {},
    sportName: String = "all",
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    communities: List<com.arena_web.data.dto.ChatListItemDto> = emptyList(),
    selectedCommunityId: String? = null,
    onCommunitySelected: (String?) -> Unit = {},
    onCommunityFilterOpen: () -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    val t = LocalAppStrings.current
    // Eagerly prefetch the user's communities once so the filter icon can be
    // hidden when the list is genuinely empty (instead of waiting for the
    // first click on a button we may not even render).
    LaunchedEffect(Unit) { onCommunityFilterOpen() }

    Box(
        modifier = Modifier
            .fillMaxHeight()
            .styleModifier {
                // Break out of MobilePageLayout's 16px horizontal padding so
                // the backdrop image can bleed edge-to-edge. The inner Column
                // below re-applies the 16px padding for the action bar / cards.
                property("position", "relative")
                property("width", "calc(100% + 32px)")
                property("margin", "0 -16px")
            }
    ) {
        // Figma 10567:202821 — sport-specific ambient backdrop. A fixed
        // 487×650 image, center and horizontally over the now-full-width Box
        // so it bleeds past the viewport edges (root Box's overflow:hidden
        // clips the overflow). opacity 0.3 tones the bright pitch art so it
        // melts into the body composite (#02040B + blue tint) and no extra
        // vertical gradient overlay is needed for the header transition.
        Img(
            src = AppIcons.mobileEventBackground(sportName),
            attrs = Modifier.styleModifier {
                // object-fit: contain anchored to the top. The PNG aspect
                // (1086×1448 ≈ 0.75) is less portrait than the mobile content
                // area (≈ 0.51), so cover would crop the sides. Contain shows
                // the whole pitch at full viewport width without distortion;
                // the remainder below it falls back to the body composite.
                property("position", "absolute")
                property("inset", "0")
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "contain")
                // 0 % vertical — pitch lifts up to the container's top edge.
                property("object-position", "center 0%")
                property("opacity", "0.1")
                property("pointer-events", "none")
                // Soft top + bottom fade into the body composite. The top fade
                // is extended further down (0 % → 32 %) so the upper third of
                // the pitch reads as darker / more dissolved against the
                // header area; the bottom fade stays softer.
                val mask = "linear-gradient(180deg, " +
                    "transparent 0%, " +
                    "black 32%, " +
                    "black 78%, " +
                    "transparent 100%)"
                property("mask-image", mask)
                property("-webkit-mask-image", mask)
            }.toAttrs()
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(leftRight = 16.px)
                .gap(16.px)
                // Without `position: relative` the Column paints in the static
                // layer, below the absolutely-positioned Img above. Even at
                // opacity 0.3 the Img then visually dims the buttons. Joining
                // the positioned layer puts the Column AFTER Img in document
                // order → content paints over the backdrop.
                .styleModifier { property("position", "relative") }
        ) {
        // Compact action bar
        MobileArenaActionBar(
            activeTab = activeTab,
            onTabSelected = onTabSelected,
            onAddEvent = onAddEvent,
            onMapClick = onMapClick,
            filterCount = filterCount,
            onFilterClick = onFilterClick,
            cities = cities,
            cityFlags = cityFlags,
            selectedCity = selectedCity,
            onCitySelected = onCitySelected,
            communities = communities,
            selectedCommunityId = selectedCommunityId,
            onCommunitySelected = onCommunitySelected,
            onCommunityFilterOpen = onCommunityFilterOpen,
            selectedDate = selectedDate,
            onDateSelected = onDateSelected
        )

        if (activeTab == "Events") {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().flexGrow(1))
            } else if (events.isEmpty()) {
                MobileEmptyState(onAddEvent = onAddEvent)
            } else {
                MobileEventGrid(
                    events = events,
                    isLoadingMore = isLoadingMore,
                    onEventClick = onEventClick,
                    onLoadMore = onLoadMore
                )
            }
        } else {
            if (isLoadingClubs) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.arenaLoadingClubs,
                        modifier = Modifier.fontSize(14.px).color(AppColors.Text.Secondary)
                    )
                }
            } else if (clubs.isEmpty()) {
                MobileEmptyClubsState()
            } else {
                MobileClubGrid(
                    clubs = clubs,
                    isLoadingMore = isLoadingMoreClubs,
                    onClubClick = onClubClick,
                    onLoadMore = onLoadMoreClubs
                )
            }
        }
        }
    }
}

@Composable
private fun MobileArenaActionBar(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    onAddEvent: () -> Unit,
    onMapClick: () -> Unit = {},
    filterCount: Int = 0,
    onFilterClick: () -> Unit = {},
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    communities: List<com.arena_web.data.dto.ChatListItemDto> = emptyList(),
    selectedCommunityId: String? = null,
    onCommunitySelected: (String?) -> Unit = {},
    onCommunityFilterOpen: () -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left group — Calendar + Community dropdown triggers + Location pill.
        Row(
            modifier = Modifier.gap(8.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            MobileCalendarButton(
                selectedDate = selectedDate,
                onDateSelected = onDateSelected
            )
            if (communities.isNotEmpty() || selectedCommunityId != null) {
                MobileCommunityButton(
                    communities = communities,
                    selectedCommunityId = selectedCommunityId,
                    onCommunitySelected = onCommunitySelected,
                    onOpen = onCommunityFilterOpen
                )
            }
            MobileLocationPill(
                cities = cities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = onCitySelected
            )
        }

        // Right group — Map + Add.
        Row(
            modifier = Modifier.gap(8.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            MobileRoundButton(iconSrc = AppIcons.Map, iconSize = 14.px, onClick = onMapClick)
            MobileRoundButton(iconSrc = AppIcons.Add, iconSize = 12.px, solid = true, onClick = onAddEvent)
        }
    }
}

@Composable
private fun MobileLocationPill(
    cities: List<String>,
    cityFlags: Map<String, String>,
    selectedCity: String?,
    onCitySelected: (String?) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    val selectedFlag = selectedCity?.let { cityFlags[it].orEmpty() } ?: ""
    val displayValue = selectedCity ?: t.arenaAllCities

    // Stable DOM id so we can detect outside clicks — same trick as the
    // desktop FilterPillDropdown (fixed-position backdrop is trapped by
    // ancestor `transform`s on the action bar).
    val dropdownId = remember { "mlp-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .height(36.px)
                .borderRadius(100.px)
                .padding(leftRight = 14.px)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.arenaLocationLabel,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(500)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("white-space", "nowrap") }
            )
            if (selectedFlag.isNotEmpty()) {
                SpanText(
                    selectedFlag,
                    modifier = Modifier
                        .fontSize(13.px)
                        .padding(right = 4.px)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("line-height", "1")
                            property("transform", "translateY(1px)")
                        }
                )
            }
            SpanText(
                displayValue,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(500)
                    .color(sportColors.primary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }

        if (expanded) {
            com.arena_web.ui.components.dropdown.LocationDropdownPanel(
                selectedCity = selectedCity,
                cities = cities,
                cityFlags = cityFlags,
                onSelect = { city ->
                    onCitySelected(city)
                    expanded = false
                },
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .left(0.px)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            )
        }
    }
}

@Composable
private fun LocationDropdownRow(
    label: String,
    flag: String,
    isSelected: Boolean,
    primary: KobwebColor,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(8.px)
            .padding(10.px, 12.px)
            .cursor(Cursor.Pointer)
            .gap(6.px)
            .styleModifier {
                if (isSelected) property("background", "rgba(0, 97, 255, 0.12)")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (flag.isNotEmpty()) {
            SpanText(
                flag,
                modifier = Modifier
                    .fontSize(14.px)
                    .styleModifier {
                        property("line-height", "1")
                        property("transform", "translateY(1px)")
                    }
            )
        }
        SpanText(
            label,
            modifier = Modifier
                .fontSize(14.px)
                .fontWeight(500)
                .color(if (isSelected) primary else AppColors.Text.Secondary)
        )
    }
}

// Outside-click handler shared by the mobile dropdown triggers. Same trick as
// the desktop FilterPillDropdown — `position:fixed` backdrop is trapped by an
// ancestor `transform`, so we listen on document instead.
@Composable
private fun OutsideClickHandler(dropdownId: String, expanded: Boolean, onClose: () -> Unit) {
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) onClose()
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }
}

@Composable
private fun MobileCalendarButton(
    selectedDate: String?,
    onDateSelected: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val dropdownId = remember { "mcal-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    OutsideClickHandler(dropdownId, expanded) { expanded = false }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        MobileRoundButton(
            iconSrc = AppIcons.Calendar,
            onClick = { expanded = !expanded }
        )
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .zIndex(100)
                    .styleModifier {
                        // Width = the DatePicker's intrinsic design size at
                        // current scale (~560 design px). Clamped to viewport
                        // minus 32px gutters so it never overflows.
                        property("--scale", "min(0.62px, calc((100vw - 32px) / 560))")
                        property("width", "calc(560 * var(--scale))")
                        property("box-sizing", "border-box")
                        // #09142d rounded card matching the Location dropdown
                        // (LocationDropdownPanel); the picker is rendered `bare`.
                        property("background", "#09142d")
                        property("border-radius", "24px")
                        // Vertical only — the day strip scrolls edge-to-edge.
                        property("padding", "12px 0")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        // Center on the viewport regardless of where the
                        // trigger button sits. Wrapper is at viewport-left +
                        // 16px, so to put the dropdown midpoint at 50vw we
                        // anchor left at (50vw - 16px) and pull back 50% of
                        // the dropdown's own width.
                        property("left", "calc(50vw - 16px)")
                        property("transform", "translateX(-50%)")
                    }
            ) {
                com.arena_web.ui.components.forms.DatePicker(
                    selectedDate = selectedDate ?: "",
                    onDateSelected = { date ->
                        onDateSelected(date)
                        expanded = false
                    },
                    bare = true
                )
            }
        }
    }
}

@Composable
private fun MobileCommunityButton(
    communities: List<com.arena_web.data.dto.ChatListItemDto>,
    selectedCommunityId: String?,
    onCommunitySelected: (String?) -> Unit,
    onOpen: () -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    val dropdownId = remember { "mcom-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    OutsideClickHandler(dropdownId, expanded) { expanded = false }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        MobileRoundButton(
            iconSrc = AppIcons.People,
            onClick = {
                val nowOpen = !expanded
                expanded = nowOpen
                if (nowOpen) onOpen()
            }
        )
        if (expanded) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .left(0.px)
                    .minWidth(200.px)
                    .borderRadius(12.px)
                    .padding(6.px)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            ) {
                LocationDropdownRow(
                    label = t.arenaCommunityAll,
                    flag = "",
                    isSelected = selectedCommunityId == null,
                    primary = sportColors.primary,
                    onClick = {
                        onCommunitySelected(null)
                        expanded = false
                    }
                )
                communities.forEach { community ->
                    LocationDropdownRow(
                        label = community.name ?: t.arenaCommunityUnnamed,
                        flag = "",
                        isSelected = community.id == selectedCommunityId,
                        primary = sportColors.primary,
                        onClick = {
                            onCommunitySelected(community.id)
                            expanded = false
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun MobileRoundButton(
    iconSrc: String,
    iconSize: CSSLengthValue = 16.px,
    solid: Boolean = false,
    onClick: () -> Unit = {}
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .size(36.px)
            .flexShrink(0)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (solid) Modifier.backgroundColor(sportColors.primary)
                else Modifier.styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
            ),
        contentAlignment = Alignment.Center
    ) {
        // Icon tint: solid Add → white-on-primary via iconFilterOnPrimary;
        // filter buttons → muted nav blue #B4CCF6 via IconDefaultFilter
        // (matches navigation icon treatment).
        Icon(
            src = iconSrc,
            modifier = Modifier.size(iconSize).styleModifier {
                property(
                    "filter",
                    if (solid) sportColors.iconFilterOnPrimary else AppColors.IconDefaultFilter
                )
            }
        )
    }
}

@Composable
private fun MobileClubsEventsSwitch(activeTab: String = "Events", onTabSelected: (String) -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .height(36.px)
            .borderRadius(18.px)
            .padding(2.px, 3.px)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        listOf("Clubs", "Events").forEach { tab ->
            val isActive = tab == activeTab
            Box(
                modifier = Modifier
                    .height(30.px)
                    .borderRadius(15.px)
                    .padding(leftRight = 16.px)
                    .then(
                        if (isActive) Modifier.backgroundColor(sportColors.primary)
                        else Modifier
                    )
                    .cursor(Cursor.Pointer)
                    .onClick { onTabSelected(tab) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (tab == "Clubs") t.arenaTabClubs else t.arenaTabEvents,
                    modifier = Modifier
                        .fontSize(13.px)
                        .fontWeight(500)
                        .color(if (isActive) sportColors.onPrimary else AppColors.Text.Secondary)
                )
            }
        }
    }
}

private const val MOBILE_GRID_SCROLL_ID = "mobile-event-grid-scroll"
private const val MOBILE_GRID_SCROLL_KEY = "arena_mobile_events_scroll_top"

@Composable
private fun MobileEventGrid(
    events: List<ArenaEvent>,
    isLoadingMore: Boolean = false,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    var restoredScroll by remember { mutableStateOf(false) }

    // Restore the previous scroll position once events are rendered. Keyed
    // on the events-list emptiness flag so it fires exactly once per fresh
    // mount with content — coming back from the event detail page lands here
    // with the same cached list and the user's previous offset is restored.
    LaunchedEffect(events.isNotEmpty()) {
        if (restoredScroll || events.isEmpty()) return@LaunchedEffect
        val saved = kotlinx.browser.window.sessionStorage.getItem(MOBILE_GRID_SCROLL_KEY)
            ?.toDoubleOrNull() ?: return@LaunchedEffect
        // Wait one frame so the cards are actually painted before we set
        // scrollTop — otherwise the container's scrollHeight is still 0 and
        // the assignment is silently clamped to 0.
        kotlinx.browser.window.requestAnimationFrame {
            kotlinx.browser.document.getElementById(MOBILE_GRID_SCROLL_ID)?.scrollTop = saved
            restoredScroll = true
        }
    }

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(MOBILE_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        var savePending = false
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
            // Throttle the sessionStorage write to one per animation frame —
            // scroll fires per-pixel and synchronous storage writes would
            // stutter mid-scroll on low-end devices.
            if (!savePending) {
                savePending = true
                kotlinx.browser.window.requestAnimationFrame {
                    kotlinx.browser.window.sessionStorage.setItem(
                        MOBILE_GRID_SCROLL_KEY,
                        el.scrollTop.toString()
                    )
                    savePending = false
                }
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .id(MOBILE_GRID_SCROLL_ID)
            .styleModifier {
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "12px")
                // Cancel the parent Column's 16px gap so the scroll edge sits
                // flush with the action bar; padding-top re-establishes the
                // first-card offset *inside* the scrollable region so the card
                // scrolls past the action bar when the user drags up.
                property("margin-top", "-16px")
                property("padding-top", "16px")
                // NOTE: padding-bottom is ignored by Chrome/Safari at the end
                // of a flex+overflow scroll container — we add an explicit
                // spacer Div below instead so the last card clears the
                // fixed BottomNavBar.

                // Soft fade at the top so cards melt out into the action bar
                // instead of clipping with a hard edge as they scroll up.
                // The fade band sits inside the 16px padding-top + ~12px of
                // the first card, giving a 28px gradient transition.
                val topFade = "linear-gradient(to bottom, transparent 0px, black 28px, black 100%)"
                property("mask-image", topFade)
                property("-webkit-mask-image", topFade)
            }
            .toAttrs()
    ) {
        events.forEach { event ->
            MobileEventCard(
                event = event,
                onClick = { onEventClick(event) }
            )
        }

        if (isLoadingMore) {
            Div(
                attrs = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("text-align", "center")
                        property("padding", "16px")
                    }
                    .toAttrs()
            ) {
                SpanText(
                    t.arenaLoadingMoreEvents,
                    modifier = Modifier.fontSize(14.px).color(AppColors.Text.Secondary)
                )
            }
        }

        // Bottom spacer — lifts the last card well above the fixed BottomNavBar.
        // Real Div (not padding) because flex+overflow:auto containers ignore
        // padding-bottom at the scroll end on Chrome/Safari.
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "0 0 200px")
                    property("height", "200px")
                }
                .toAttrs()
        )
    }
}

// =============================================================================
// Mobile Event Card — Figma 10032:208767 (single-column list item)
// =============================================================================

@Composable
private fun MobileEventCard(
    event: ArenaEvent,
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val isCompleted = event.status == EventStatus.COMPLETED || event.status == EventStatus.CANCELED
    val dateColor = if (isCompleted) AppColors.Text.Muted else AppColors.PrimaryWhite
    val metaColor = if (isCompleted) AppColors.Text.Muted else KobwebColor.hex("BED6F5")
    val subtitleColor = if (isCompleted) AppColors.Text.Muted else KobwebColor.hex("B9B9B9")

    // Figma 10032:208803 — card 358×204, border 0.3px solid rgba(255,255,255,0.4),
    // radius 16, overlay gradient 135.88deg (dark→fade), content px-12 with column
    // gap-16, date row + name/location (gap-6) and bottom meta block (gap-4).
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(204.px)
            .flexShrink(0)
            .overflow(Overflow.Hidden)
            .borderRadius(16.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "url('${event.imageUrl}') center/cover no-repeat padding-box, " +
                        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
    ) {
        // Legibility overlay — diagonal dark-to-fade from top-left.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .styleModifier {
                    property(
                        "background",
                        if (isCompleted) "rgba(2, 4, 11, 0.82)"
                        else "linear-gradient(135.88deg, rgba(0, 0, 0, 0.8) 1.93%, rgba(0, 0, 0, 0.5) 53.38%, rgba(153, 153, 153, 0) 94.11%)"
                    )
                }
        )

        // Status badge — top-right (Figma: h28, px16 py8, radius 22).
        Box(
            modifier = Modifier.position(Position.Absolute).top(9.px).right(14.px)
        ) {
            MobileEventBadge(event.status)
        }

        // Content — top-left, 12px horizontal padding, 16px top.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .gap(16.px)
                .styleModifier { property("padding", "16px 12px 6px 12px") }
        ) {
            // Headline group (date + name/location), gap 8 between rows.
            Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                // Date • time (Figma: 24px Medium)
                Row(
                    modifier = Modifier.gap(8.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        event.date,
                        modifier = Modifier.fontSize(24.px).fontWeight(500).lineHeight(32.px).color(dateColor)
                    )
                    Box(modifier = Modifier.size(4.px).borderRadius(50.percent).backgroundColor(dateColor))
                    SpanText(
                        event.time,
                        modifier = Modifier.fontSize(24.px).fontWeight(500).lineHeight(32.px).color(dateColor)
                    )
                }

                // Name / club / location group (gap 6).
                Column(modifier = Modifier.fillMaxWidth().gap(6.px)) {
                    if (event.title.isNotBlank()) {
                        SpanText(
                            event.title,
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(18.px)
                                .fontWeight(500)
                                .lineHeight(22.px)
                                .color(metaColor)
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                    if (event.location.isNotBlank()) {
                        SpanText(
                            event.location,
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(12.px)
                                .lineHeight(12.px)
                                .color(subtitleColor)
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                }
            }

            // Bottom meta — members + format on one row, cost beneath (gap 4).
            Column(modifier = Modifier.gap(4.px)) {
                val membersText = when {
                    event.membersJoined != null && event.membersTotal != null ->
                        t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal)
                    event.membersJoined != null -> t.arenaMembersCount(event.membersJoined)
                    event.membersTotal != null -> t.arenaMembersCount(event.membersTotal)
                    else -> null
                }
                Row(
                    modifier = Modifier.gap(18.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (membersText != null) MobileMetaChip(AppIcons.People, membersText, metaColor)
                    if (event.format != null) MobileMetaChip(AppIcons.FootballField, t.arenaFormat(event.format), metaColor)
                }
                if (event.cost != null) MobileMetaChip(AppIcons.Card, event.cost, metaColor)
            }
        }
    }
}

@Composable
private fun MobileMetaChip(iconSrc: String, text: String, color: com.varabyte.kobweb.compose.ui.graphics.Color) {
    Row(
        modifier = Modifier.gap(6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = iconSrc, modifier = Modifier.size(16.px))
        SpanText(
            text,
            modifier = Modifier.fontSize(12.px).fontWeight(500).lineHeight(12.px).color(color)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun MobileEventBadge(status: EventStatus) {
    val t = LocalAppStrings.current
    if (status == EventStatus.IN_PROGRESS) {
        Row(
            modifier = Modifier.gap(6.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.size(6.px).borderRadius(50.percent).backgroundColor(KobwebColor.rgb(242, 85, 85)))
            SpanText(t.arenaStatusLive, modifier = Modifier.fontSize(12.px).fontWeight(500).color(AppColors.PrimaryWhite))
        }
        return
    }

    val bgColor: com.varabyte.kobweb.compose.ui.graphics.Color
    val textColor: com.varabyte.kobweb.compose.ui.graphics.Color
    val label: String
    when (status) {
        EventStatus.NEW -> {
            bgColor = KobwebColor.rgb(0, 97, 255)
            textColor = KobwebColor.rgb(255, 255, 255)
            label = t.arenaStatusNew
        }
        EventStatus.COMPLETED -> {
            bgColor = KobwebColor.rgb(52, 213, 36)
            textColor = KobwebColor.rgb(0, 0, 0)
            label = t.arenaStatusCompleted
        }
        EventStatus.CANCELED -> {
            bgColor = KobwebColor.rgb(242, 85, 85)
            textColor = KobwebColor.rgb(255, 255, 255)
            label = t.arenaStatusCanceled
        }
        EventStatus.IN_PROGRESS -> return
    }

    // Figma 10032:208849 — h28, px16 py8, radius 22, label 14px Regular lh22.
    Box(
        modifier = Modifier
            .height(28.px)
            .backgroundColor(bgColor)
            .borderRadius(22.px)
            .padding(leftRight = 16.px, topBottom = 8.px),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier.fontSize(14.px).lineHeight(22.px).color(textColor)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// =============================================================================
// Mobile Club Grid
// =============================================================================

private const val MOBILE_CLUB_GRID_SCROLL_ID = "mobile-club-grid-scroll"

@Composable
private fun MobileClubGrid(
    clubs: List<ClubDto>,
    isLoadingMore: Boolean = false,
    onClubClick: (ClubDto) -> Unit = {},
    onLoadMore: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(MOBILE_CLUB_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .id(MOBILE_CLUB_GRID_SCROLL_ID)
            .styleModifier {
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
                property("display", "grid")
                property("grid-template-columns", "1fr 1fr")
                property("gap", "12px")
                property("align-content", "start")
            }
            .toAttrs()
    ) {
        clubs.forEach { club ->
            MobileClubCard(
                club = club,
                onClick = { onClubClick(club) }
            )
        }

        if (isLoadingMore) {
            Div(
                attrs = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("grid-column", "1 / -1")
                        property("text-align", "center")
                        property("padding", "16px")
                    }
                    .toAttrs()
            ) {
                SpanText(
                    t.arenaLoadingMoreClubs,
                    modifier = Modifier.fontSize(14.px).color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

@Composable
private fun MobileClubCard(
    club: ClubDto,
    onClick: () -> Unit = {}
) {
    val imageUrl = club.mainPhotoUrl ?: "/icons/event_football_field.png"

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(200.px)
            .overflow(Overflow.Hidden)
            .borderRadius(12.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("background",
                    "url('$imageUrl') center/cover no-repeat padding-box, " +
                    "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            }
    ) {
        // Bottom gradient overlay
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(120.px)
                .position(Position.Absolute)
                .bottom(0.px).left(0.px)
                .styleModifier {
                    property("background", "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%)")
                }
        )

        // Club info at bottom
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px)
                .padding(12.px)
                .gap(4.px)
        ) {
            SpanText(
                club.name,
                modifier = Modifier
                    .fontSize(14.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(AppColors.PrimaryWhite)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
            )
            SpanText(
                club.address,
                modifier = Modifier
                    .fontSize(12.px)
                    .color(AppColors.Text.Subtle)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
            )
            if (club.averageRating > 0) {
                Row(
                    modifier = Modifier.gap(4.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(src = AppIcons.StarRating16, modifier = Modifier.size(14.px))
                    SpanText(
                        club.averageRating.asDynamic().toFixed(1) as String,
                        modifier = Modifier.fontSize(12.px).color(AppColors.Text.Secondary)
                    )
                }
            }
        }
    }
}

// =============================================================================
// Mobile Empty States
// =============================================================================

@Composable
private fun MobileEmptyState(onAddEvent: () -> Unit = {}) {
    // Figma 10567:190928 typography & sizes; top padding 100 px (user pref).
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .mPaddingTop(100)
            .mGap(28),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().mGap(8),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Title — Body/2XL/M (Urbanist Medium 22/26, ls 0.22).
            SpanText(
                t.arenaEmptyTitle,
                modifier = Modifier
                    .mFontSize(22)
                    .fontWeight(500)
                    .mLineHeight(26)
                    .mLetterSpacing(0.22)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("white-space", "nowrap") }
            )
            // Subtitle — Body/L/R (Urbanist Regular 16/24 = lh 1.5, ls 0.16).
            SpanText(
                t.arenaEmptySubtitle,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(400)
                    .mLineHeight(24)
                    .mLetterSpacing(0.16)
                    .color(AppColors.Text.Secondary)
                    .textAlign(TextAlign.Center)
            )
        }

        // Add Event button — 180×44, primary fill, radius 22, padding 24×10.
        Box(
            modifier = Modifier
                .mWidth(180)
                .mHeight(44)
                .mBorderRadius(22)
                .mPadding(10, 24)
                .backgroundColor(sportColors.primary)
                .cursor(Cursor.Pointer)
                .onClick { onAddEvent() },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.arenaAddEvent,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(500)
                    .mLineHeight(18)
                    .mLetterSpacing(0.032)
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun MobileEmptyClubsState() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.gap(20.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(72.px)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Location, modifier = Modifier.size(32.px))
            }

            Column(
                modifier = Modifier.gap(6.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    t.arenaNoClubsTitle,
                    modifier = Modifier
                        .fontSize(18.px)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Primary)
                )
                SpanText(
                    t.arenaNoClubsSubtitle,
                    modifier = Modifier
                        .fontSize(14.px)
                        .color(AppColors.Text.Secondary)
                )
            }
        }
    }
}
